import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import ENV from 'frontend-login/config/environment';
import type { ModelFrom } from 'frontend-login/utils/routes';
import { getHostDomain } from 'frontend-utils/utils/domain-checks';
import type { MfaInfo } from 'password-form/services/mfa-service';

export type Model = ModelFrom<MfaRoute>;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
async function userIsAuthenticated() {
  const host = getHostDomain({ frontendGateway: ENV.frontendGateway });
  const userRequest = await fetch(`${host}/api/v3/users/?only_self=true`, {
    credentials: 'include',
  });

  return userRequest.ok;
}

export default class MfaRoute extends Route {
  @service declare alerting: Services['alerting'];
  @service declare customFetch: Services['custom-fetch'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare router: Services['router'];
  @service declare sharedAuth: Services['shared-auth'];

  async beforeModel(): Promise<void> {
    if (await userIsAuthenticated()) {
      this.redirectToRoot();
      return;
    }
  }

  async model(): Promise<{
    email: string;
    mfaInfo: MfaInfo;
  }> {
    const email = this.sharedAuth.authenticatedEmail;

    if (!email) {
      this.router.transitionTo('login');
      throw new Error('Email is not available');
    }

    // Attach the user's email to Sentry so MFA errors (e.g. SMS region blocks)
    // can be traced back to the affected user.
    this.errorTracking.setUser({ email });

    const mfaInfo = await this.requestMfaInfo(email);

    return {
      email,
      mfaInfo,
    };
  }

  redirectToRoot(): void {
    window.location.replace('/');
  }

  requestMfaInfo(email: string): Promise<MfaInfo> {
    try {
      return this.customFetch.fetch('/sso/mfa-info/', {
        email,
      });
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
      this.errorTracking.captureException(error);
      throw new Error('MFA info is not available');
    }
  }
}
